import { Link as RouterLink } from 'react-router-dom'
import { trackCTAClick } from '../services/analytics'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Icon,
} from '@chakra-ui/react'
import { FaFilePdf } from 'react-icons/fa'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import SEO from './SEO.jsx'

const Section = ({ title, children }) => (
  <Box as="section">
    <Heading as="h2" size="lg" color="purple.700" mb={3}>
      {title}
    </Heading>
    <VStack align="stretch" gap={3} color="gray.700" fontSize="md" lineHeight="tall">
      {children}
    </VStack>
  </Box>
)

const ManualUsuario = () => (
  <Box>
    <SEO
      title="Manual de usuario"
      description="Cómo registrar y validar tu banda en Cadencya, montar la plantilla, el archivo, los ensayos y tu web pública."
      path="/manual"
    />
    <Header />
    <Box bg="purple.600" color="white" py={{ base: 10, md: 14 }}>
      <Container maxW="3xl">
        <VStack align="flex-start" gap={4}>
          <Heading as="h1" fontSize={{ base: '3xl', md: '4xl' }} fontWeight="800">
            Manual de usuario
          </Heading>
          <Text fontSize="lg" color="purple.100">
            Guía de inicio para bandas. Cadencya es el sistema de la agrupación:
            Gestión, Archivo y Tu Web.
          </Text>
          <Button
            bg="white"
            color="purple.700"
            rounded="full"
            fontWeight="700"
            asChild
          >
            <a
              href="/manual-usuario-cadencya.pdf?v=20260927"
              download
              onClick={() => trackCTAClick('manual_pdf', 'manual_page')}
            >
              <Icon as={FaFilePdf} />
              Descargar PDF
            </a>
          </Button>
        </VStack>
      </Container>
    </Box>

    <Box bg="white" py={{ base: 12, md: 16 }}>
      <Container maxW="3xl">
        <VStack align="stretch" gap={10}>
          <Section title="1. Crea la cuenta de tu banda">
            <Text>
              Entra en Cadencya, pulsa «Soy una banda» y completa el registro. En minutos
              tienes la cuenta de la agrupación. El día a día (músicos, partituras, ensayos)
              vive en Gestión; no hace falta usar la comunidad para empezar.
            </Text>
          </Section>

          <Section title="2. Valida tu agrupación">
            <Text>
              Hay un paso intermedio y es importante: completa los datos obligatorios del
              perfil. Se genera un código de validación. Envíalo por mensaje directo a
              cualquiera de nuestros perfiles en redes (Instagram, Facebook, TikTok o X).
            </Text>
            <Text>
              Así confirmamos que sois una banda real. Te da seguridad a ti y a quien te
              contacte. Mientras el equipo lo revisa (suele ser en 48 horas), el perfil no
              es visible en Comunidad. Puedes ir montando Gestión igual.
            </Text>
          </Section>

          <Section title="3. Elige tu plan">
            <Text>
              Gratis, Estándar, Intermedio o Avanzado. Cada uno define archivo, capacidad
              de IA y almacenamiento. El plan Avanzado está gratis durante el lanzamiento
              para las bandas validadas. El detalle de cuotas se ve al elegir plan en la
              cuenta, en Mi suscripción.
            </Text>
          </Section>

          <Section title="4. Plantilla, archivo y calendario">
            <Text>
              Añade músicos y formaciones. Sin plantilla no se pueden programar ensayos ni
              actuaciones. Sube partituras al archivo: la IA ayuda a etiquetar y organizar
              por obra, carpeta y voz. Programa ensayos y actuaciones, pasa lista y comparte
              encuestas de asistencia.
            </Text>
          </Section>

          <Section title="5. Permisos, documentos y Tu Web">
            <Text>
              Invita a colaboradores con permisos concretos (archivo, músicos, ensayos…).
              Usa la biblioteca interna y, si quieres, un tablón de documentos con
              contraseña para la agrupación. Publica el micrositio de la banda: hay nueve
              plantillas. El dominio propio va con el plan Avanzado.
            </Text>
          </Section>

          <Section title="6. Comunidad (opcional)">
            <Text>
              La comunidad es una funcionalidad más: perfil público, eventos, chat y
              acuerdos con hermandades y entidades. No es el producto principal. Cuando
              la agrupación esté validada, el perfil puede verse en el directorio.
            </Text>
          </Section>

          <Section title="Si representas a una hermandad">
            <Text>
              Esta guía está pensada para bandas. Para encontrar y contactar agrupaciones,
              usa la página de hermandades.
            </Text>
            <Button variant="outline" rounded="full" alignSelf="flex-start" asChild>
              <RouterLink to="/hermandades">Ir a hermandades</RouterLink>
            </Button>
          </Section>

          <HStack gap={3} flexWrap="wrap">
            <Button colorPalette="purple" rounded="full" asChild>
              <RouterLink to="/como-funciona">Cómo funciona, paso a paso</RouterLink>
            </Button>
            <Button variant="outline" rounded="full" asChild>
              <RouterLink to="/ayuda">Centro de ayuda</RouterLink>
            </Button>
          </HStack>
        </VStack>
      </Container>
    </Box>
    <Footer />
  </Box>
)

export default ManualUsuario
